// Staff-only gate for legacy after the V2 go-live (Maayan, decided 18-Oct-2026, re-confirmed 03-Nov-2026).
//
// After go-live the investor platform is V2 on https://www.iangels.com. Legacy (app.iangels.com) stays up as
// the staff tool and the fallback. When `staff_only_gate` is on in this environment's config, the account that
// is REALLY signed in must be staff, or it is signed out of legacy and sent to `staff_gate_redirect_url`.
//
// Staff = (1) an address on exactly the domain @iangels.com, or (2) an address on `staff_allow_list` (exact,
// case-insensitive, trimmed), or (3) a Gmail "+" alias of an address on `staff_plus_alias_bases` (only those
// bases — there is no general alias or wildcard rule).
//
// "Really signed in": during impersonation the token belongs to the INVESTOR. Auth0 impersonation (started by the
// Salesforce Impersonate buttons → services.iangels.co/details) puts `impersonated: true` and
// `impersonator: { user_id, email }` in that token (iangels-services app/controllers/home_controller.rb requests
// the `impersonated impersonator` scopes; backend core/auth/auth0-user-creator.rb reads both claims). So in an
// impersonation the impersonator's email is checked, never the investor's.
//
// This is not a security boundary for investor data — the legacy API still authorises every request per user.
// It sends investors to V2. Detail: angels67-api-v2 docs/APP_IANGELS_REDIRECT_RUNBOOK.md.

export const STAFF_EMAIL_DOMAIN = 'iangels.com';

// Paths that legacy keeps serving to everyone (the edge passes them too): the public founder form and the
// Auth0 tenant error page. A signed-in investor there is not sent away.
const OPEN_PATHS = /^\/(founder|foundersubmitted|error)(\/|$)/;

// The gate sends one tab to V2 at most once in this window. Coming back sooner means V2's login sent the user
// back here (e.g. before go-live or after a rollback www.iangels.com/login → app.iangels.com/login): stop and show
// a message instead of looping.
export const LOOP_WINDOW_MS = 2 * 60 * 1000;
const SENT_AT_KEY = 'legacy_staff_gate_sent_at';

export const normalizeEmail = (email) => (typeof email === 'string' ? email.trim().toLowerCase() : '');

const splitEmail = (email) => {
  const e = normalizeEmail(email);
  const at = e.indexOf('@');
  if (at <= 0 || at !== e.lastIndexOf('@') || at === e.length - 1 || /\s/.test(e)) return null;
  return { local: e.slice(0, at), domain: e.slice(at + 1) };
};

// Exactly @iangels.com — not a sub-domain, not a look-alike (x@iangels.com.evil, x@iangels.co, x@myiangels.com).
export const isStaffDomain = (email) => {
  const parts = splitEmail(email);
  return !!parts && parts.domain === STAFF_EMAIL_DOMAIN;
};

export const isOnAllowList = (email, allowList) => {
  const e = normalizeEmail(email);
  if (!e || !splitEmail(e)) return false;
  return (allowList || []).some((listed) => normalizeEmail(listed) === e);
};

// base "maayansasson@gmail.com" → "maayansasson+anything@gmail.com" passes (same for each listed base); the base itself is NOT matched here
// (list it on the allow-list), and nothing else is.
export const isPlusAliasOf = (email, bases) => {
  const parts = splitEmail(email);
  if (!parts) return false;
  const plus = parts.local.indexOf('+');
  if (plus <= 0 || plus === parts.local.length - 1) return false;
  const candidate = `${parts.local.slice(0, plus)}@${parts.domain}`;
  return (bases || []).some((base) => {
    const b = splitEmail(base);
    return !!b && b.local.indexOf('+') === -1 && `${b.local}@${b.domain}` === candidate;
  });
};

export const isStaffEmail = (email, rules = {}) =>
  isStaffDomain(email) || isOnAllowList(email, rules.allowList) || isPlusAliasOf(email, rules.plusAliasBases);

export const staffRulesFromConfig = (configModule = {}) => ({
  allowList: configModule.staff_allow_list || [],
  plusAliasBases: configModule.staff_plus_alias_bases || [],
});

export const isGateEnabled = (configModule = {}) => configModule.staff_only_gate === true;

const impersonationClaims = (...sources) => {
  for (const s of sources) {
    if (s && s.impersonated === true) return { impersonated: true, impersonatorEmail: s.impersonator?.email || null };
  }
  return { impersonated: false, impersonatorEmail: null };
};

/**
 * Decide for the current page: 'allow' | 'deny' | 'wait'.
 *
 * @param {object} p
 * @param {boolean} p.enabled          the env's staff_only_gate
 * @param {string}  p.pathname         window.location.pathname
 * @param {object}  p.auth0User        useAuth0().user — the SPA's own Auth0 session (null when none)
 * @param {object}  p.impersonatedUser claims of the id_token handed over on /imper (Impersonate container)
 * @param {object}  p.traits           what legacy's API returned for the signed-in token (identity/create)
 * @param {boolean} p.impersonationHandoff legacy's own redux `impersonated` flag (set on /#access_token → /imper)
 * @param {object}  p.rules            staffRulesFromConfig(config)
 */
export const decideLegacyAccess = ({
  enabled,
  pathname,
  auth0User,
  impersonatedUser,
  traits,
  impersonationHandoff,
  rules,
}) => {
  if (!enabled) return 'allow';
  if (OPEN_PATHS.test(pathname || '/')) return 'allow';

  // The impersonation hand-off (SF Impersonate → services /details → Auth0 → /#access_token → /imper). On staging
  // (03-Nov-2026) its id_token carried NO `impersonated` / `impersonator` claim and the access token is opaque, so
  // nothing in it names the impersonator. The hand-off itself is the signal: let it through (Maayan, 03-Nov-2026),
  // accepting that the gate cannot check WHO impersonates. Both must hold, so a stale flag alone lets no one in.
  if (impersonationHandoff === true && impersonatedUser) return 'allow';

  const imp = impersonationClaims(impersonatedUser, auth0User);
  if (imp.impersonated) {
    // A real Auth0 impersonation (only services /details can start one). Check WHO impersonates.
    if (imp.impersonatorEmail) return isStaffEmail(imp.impersonatorEmail, rules) ? 'allow' : 'deny';
    // The claim names no email: fall back to the SPA's own staff session, else let it through (an impersonation
    // is staff-started; blocking it would cut staff off from investor support).
    if (auth0User?.email && auth0User.impersonated !== true) return isStaffEmail(auth0User.email, rules) ? 'allow' : 'deny';
    return 'allow';
  }

  if (!auth0User && !impersonatedUser) return 'allow'; // nobody signed in — the login flow handles it

  const email = auth0User?.email || impersonatedUser?.email || traits?.email;
  if (email) return isStaffEmail(email, rules) ? 'allow' : 'deny';
  // Signed in but no email yet: the id_token may lack it; legacy's API answers with traits.email.
  const traitsLoaded = traits && Object.keys(traits).length > 0;
  return traitsLoaded ? 'deny' : 'wait';
};

// --- leaving legacy ---------------------------------------------------------------------------------------------

let leaving = false;
// True once the gate has started signing this page out: the login / logout effects must not race it.
export const isLeavingLegacy = () => leaving;
export const resetLeavingForTests = () => {
  leaving = false;
};

/**
 * Should the browser be sent to V2 now (true), or has this tab just come back from there (false → show a message)?
 * Records the send time in sessionStorage (per tab, survives the cross-site round trip).
 */
export const claimRedirect = (storage, now = Date.now()) => {
  leaving = true;
  let last = 0;
  try {
    last = Number(storage?.getItem(SENT_AT_KEY)) || 0;
  } catch (e) {
    last = 0;
  }
  if (last && now - last >= 0 && now - last < LOOP_WINDOW_MS) return false;
  try {
    storage?.setItem(SENT_AT_KEY, String(now));
  } catch (e) {
    // storage unavailable: still redirect once
  }
  return true;
};
